import type { DeviceState } from '@/types';
import {
  registerPairingCode,
  requestActivationChallenge,
  confirmPairing,
} from './api-client';
import {
  clearPairingCredentials,
  getDeviceCredentials,
  signWithDeviceKey,
} from './pairing';
import { secureStorage } from './secure-storage';

/**
 * Provisioning state for this player. There is no longer a "device
 * token" — the keystore-bound private key is the credential. We only
 * persist the boardId/boardCode the operator paired us with so the
 * player knows which board it represents.
 */

import { storageKey } from './storage-keys';

const KEYS = {
  BOARD_ID: storageKey('board_id'),
  BOARD_CODE: storageKey('board_code'),
};

export async function loadDeviceState(): Promise<DeviceState> {
  const [boardId, boardCode] = await Promise.all([
    secureStorage.get(KEYS.BOARD_ID),
    secureStorage.get(KEYS.BOARD_CODE),
  ]);
  return {
    boardId,
    boardCode,
    provisioned: !!(boardId && boardCode),
  };
}

/**
 * One-shot register: tells the server "here is my public key for this
 * pairing code". Idempotent on the server (re-registering the same code
 * within the active window 409s — the code is reusable across crashes
 * because the public key is stable).
 */
export async function registerWithServer(pairingCode: string): Promise<void> {
  const creds = await getDeviceCredentials();
  await registerPairingCode({ pairingCode, ...creds });
}

/**
 * Activation handshake (steps 4a + 4b in SECURITY.md):
 *   1. Ask server for a challenge.
 *   2. Sign it with the keystore key.
 *   3. POST signature → server installs our public key on the Board.
 * On success, persist boardId/boardCode and clear the pairing code.
 */
export async function activate(pairingCode: string): Promise<DeviceState> {
  const { challenge } = await requestActivationChallenge(pairingCode);

  // Server-issued 32 bytes, base64.
  const challengeBytes = base64ToBytes(challenge);
  const signature = await signWithDeviceKey(challengeBytes);

  const result = await confirmPairing(pairingCode, signature);

  await Promise.all([
    secureStorage.set(KEYS.BOARD_ID, result.boardId),
    secureStorage.set(KEYS.BOARD_CODE, result.boardCode),
  ]);
  await clearPairingCredentials();

  return {
    boardId: result.boardId,
    boardCode: result.boardCode,
    provisioned: true,
  };
}

export async function clearDeviceState(): Promise<void> {
  await Promise.all([
    secureStorage.remove(KEYS.BOARD_ID),
    secureStorage.remove(KEYS.BOARD_CODE),
  ]);
  // We deliberately do NOT clear the keystore key — that's a separate
  // "factory reset" path. Wiping it would force re-pairing without a
  // way to verify it's the same physical device.
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
